import React, { useState } from "react";
import { Trash2 } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
import CrmSwitch from "../../ui/CrmSwitch";
import CrmButton from "../../ui/CrmButton";
import CrmAlertDialog from "../../ui/CrmAlertDialog";
import OffsetField from "./OffsetField";
import EmailBodyEditor from "./EmailBodyEditor";
import { offsetText } from "./StepSummary";
import useEmailPreview from "../preview/useEmailPreview";
import EmailPreviewDialog from "../preview/EmailPreviewDialog";
import { CRM } from "../../../../constants/testIds/crm";

/**
 * One step row (spec Sequence editor): "Step {n}", Preview, Send test and Delete are
 * reachable whatever the row's open state (steps render "one expanded at a time" — spec —
 * but that governs only the subject/body/offset FORM; a collapsed row still shows its
 * summary line and keeps its own actions, so a practitioner can preview, test-send or
 * remove a step without first opening it).
 *
 * `emailDraft` is this step as an email (`useSequence().stepEmailDraft`): Preview and
 * Send test both act on the draft on screen, never on the saved copy.
 */
export default function StepEditor({ step, index, anchors, isReminders, isOpen, onToggleOpen, onChange, onDelete, emailDraft }) {
  const t = useT("crm");
  const [deleting, setDeleting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const preview = useEmailPreview(emailDraft);

  const openPreview = () => { setPreviewOpen(true); preview.load(); };

  return (
    <div data-testid={CRM.seqStep(index)} className="rounded-2xl border border-eh-border-soft bg-white p-5 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button type="button" onClick={onToggleOpen} className="font-display text-lg text-eh-ink text-left" data-testid={CRM.seqStepSummary(index)}>
          {t("seq.stepTitle", { n: index + 1 })}
          {!isOpen && <span className="ml-2 text-sm font-body text-eh-muted">· {offsetText(step.offset, t)} · {step.subject?.trim() || t("seq.stepSummaryNoBody", { n: index + 1, offset: "" })}</span>}
        </button>
        <div className="flex items-center gap-2">
          <CrmButton variant="outline" size="sm" disabled={preview.loading} onClick={openPreview} data-testid={CRM.seqStepPreview(index)}>
            {t("emailPreview.preview")}
          </CrmButton>
          <CrmButton variant="outline" size="sm" disabled={preview.sending} onClick={preview.sendTest} data-testid={CRM.seqStepSendTest(index)}>
            {t("emailPreview.sendTestToMe")}
          </CrmButton>
          <button type="button" onClick={() => setDeleting(true)} aria-label={t("seq.deleteStep")}
            className="rounded-full p-2 text-eh-muted hover:bg-eh-bg hover:text-red-700" data-testid={CRM.seqStepDelete(index)}>
            <Trash2 size={16} aria-hidden="true" />
          </button>
        </div>
      </div>

      {isOpen && (
        <>
          <OffsetField offset={step.offset} onChange={(offset) => onChange({ offset })} anchors={anchors} stepIndex={index} />

          <EmailBodyEditor
            stepIndex={index}
            subject={step.subject}
            onSubjectChange={(subject) => onChange({ subject })}
            body={step.body_html}
            onBodyChange={(body_html) => onChange({ body_html })}
            reminderButtons={isReminders}
          />

          {!isReminders && (
            <label className="flex items-center gap-3 text-sm text-eh-ink">
              <CrmSwitch checked={!!step.book_again_button} onCheckedChange={(v) => onChange({ book_again_button: v })}
                data-testid={CRM.seqStepBookAgain(index)} />
              <span className="text-xs uppercase tracking-[0.18em] text-eh-muted">{t("seq.bookAgainButton")}</span>
            </label>
          )}
        </>
      )}

      <EmailPreviewDialog open={previewOpen} onOpenChange={setPreviewOpen} preview={preview} />

      <CrmAlertDialog
        open={deleting}
        onOpenChange={setDeleting}
        tone="danger"
        testId={CRM.seqStepDeleteDialog(index)}
        title={t("seq.deleteStepConfirmTitle")}
        description={t("seq.deleteStepConfirmBody")}
        confirmLabel={t("seq.deleteStepConfirm")}
        onConfirm={() => { setDeleting(false); onDelete(); }}
        confirmTestId={CRM.seqStepDeleteConfirm(index)}
      />
    </div>
  );
}
